import { sendCommand } from './extension'
import { supabase } from './supabase'

export interface FullEnrichment {
  publicIdentifier: string | null
  firstName: string | null
  lastName: string | null
  location: string | null
  industry: string | null
  currentTitle: string | null
  currentCompany: string | null
  premium: boolean
  influencer: boolean
  creator: boolean
  verified: boolean
  connectedAt: number | null
  experience: Array<{
    title: string | null
    companyName: string | null
    companyUrn: string | null
    location: string | null
    description: string | null
    startDate: { month?: number; year: number } | null
    endDate: { month?: number; year: number } | null
    isCurrent: boolean
  }>
  education: Array<{
    schoolName: string | null
    degreeName: string | null
    fieldOfStudy: string | null
    startYear: number | null
    endYear: number | null
  }>
  skills: string[]
}

/**
 * Enrich a participant via the extension, then write the full result to Supabase.
 * The extension also broadcasts lean fields to IDB via PARTICIPANT_ENRICHED.
 *
 * Returns the enrichment data, or null on failure.
 */
export async function enrichAndSync(
  profileUrn: string,
  conversationUrn: string,
  /** Basic data from IDB to include in the Supabase row */
  basicData: {
    name: string
    headline?: string | null
    profileImage?: string | null
    lastMessageTimestamp?: number | null
  },
): Promise<FullEnrichment | null> {
  try {
    const enrichment = await sendCommand<FullEnrichment>('ENRICH_PARTICIPANT', {
      profileUrn,
      conversationUrn,
    })
    if (!enrichment) return null

    // Write full enrichment to Supabase
    const now = new Date().toISOString()
    const row = {
      profile_urn: profileUrn,
      name: basicData.name || (enrichment.firstName
        ? `${enrichment.firstName || ''} ${enrichment.lastName || ''}`.trim()
        : 'Unknown'),
      first_name: enrichment.firstName,
      last_name: enrichment.lastName,
      headline: basicData.headline || null,
      profile_image_url: basicData.profileImage || null,
      location: enrichment.location,
      industry: enrichment.industry,
      public_identifier: enrichment.publicIdentifier,
      current_title: enrichment.currentTitle,
      current_company: enrichment.currentCompany,
      premium: enrichment.premium ?? null,
      influencer: enrichment.influencer ?? null,
      creator: enrichment.creator ?? null,
      verified: enrichment.verified ?? null,
      connected_at: enrichment.connectedAt,
      experience: enrichment.experience || [],
      education: enrichment.education || [],
      skills: enrichment.skills || [],
      enriched_at: now,
      last_interaction_at: basicData.lastMessageTimestamp
        ? new Date(basicData.lastMessageTimestamp).toISOString()
        : null,
    }

    const { error } = await supabase.rpc('upsert_contacts', { payload: [row] })
    if (error) {
      console.error('[Slash] Failed to sync enriched contact to Supabase:', error.message)
    }

    return enrichment
  } catch (err) {
    console.warn('[Slash] enrichAndSync failed:', (err as Error).message)
    return null
  }
}
